import { useState, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'magic-link' ? 'magic-link' : 'password';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);

  // Magic link state
  const [magicEmail, setMagicEmail] = useState('');
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(username, password);
      toast.success('Login successful!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!magicEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setMagicLoading(true);

    try {
      await axios.post(`${API}/auth/magic-link`, { email: magicEmail.trim() });
      setMagicSent(true);
      toast.success('Check your email for the sign-in link!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send sign-in link. Please try again.');
    } finally {
      setMagicLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_action-portal/artifacts/0jx7uwn8_HLRA_logo_non_transparent.jpeg" 
              alt="HLRA Logo" 
              className="h-24 w-auto"
            />
          </div>
          <CardTitle className="text-3xl font-bold">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              HLRA Action Portal
            </span>
          </CardTitle>
          <CardDescription className="text-base">
            Holborough Lakes Residents Association
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tab Toggle */}
          <div className="flex rounded-lg bg-gray-100 p-1" data-testid="login-tab-toggle">
            <button
              type="button"
              onClick={() => { setActiveTab('password'); setMagicSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                activeTab === 'password'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-password"
            >
              <Lock className="h-4 w-4" />
              Password
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('magic-link')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                activeTab === 'magic-link'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-magic-link"
            >
              <Mail className="h-4 w-4" />
              Email Link
            </button>
          </div>

          {/* Password Login */}
          {activeTab === 'password' && (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="password-login-form" autoComplete="off">
              <div className="space-y-2">
                <Label htmlFor="username">Username or Email</Label>
                <Input
                  id="username"
                  type="text"
                  name="hlra_username"
                  placeholder="Enter your username or email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="off"
                  data-testid="username-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  name="hlra_password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="off"
                  data-testid="password-input"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-medium"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
          )}

          {/* Magic Link Login */}
          {activeTab === 'magic-link' && !magicSent && (
            <form onSubmit={handleMagicLink} className="space-y-4" data-testid="magic-link-form">
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                <p className="text-sm text-blue-700">
                  Enter your email and we'll send you a sign-in link. No password needed!
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="magic-email">Email Address</Label>
                <Input
                  id="magic-email"
                  type="email"
                  placeholder="Enter your email address"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  required
                  data-testid="magic-email-input"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-medium"
                disabled={magicLoading}
                data-testid="magic-link-submit"
              >
                {magicLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending link...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Sign-In Link
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Magic Link Sent Confirmation */}
          {activeTab === 'magic-link' && magicSent && (
            <div className="space-y-4 py-4" data-testid="magic-link-sent">
              <div className="flex flex-col items-center space-y-3">
                <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail className="h-7 w-7 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Check your email</h3>
                <p className="text-sm text-gray-500 text-center">
                  If an account exists for <strong>{magicEmail}</strong>, we've sent a sign-in link.
                </p>
                <p className="text-xs text-gray-400 text-center">
                  The link will expire in 15 minutes. Check your spam folder if you don't see it.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => { setMagicSent(false); setMagicEmail(''); }}
                className="w-full"
                data-testid="try-different-email-btn"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Try a different email
              </Button>
            </div>
          )}

          <div className="text-center text-xs text-gray-400 pt-4 border-t">
            <p>Access is by invitation only.</p>
            <p>Contact your administrator if you need an account.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
